import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Leaf, MapPin, Users, Calendar, Plus, X, Save, Clock
} from 'lucide-react'
import {
  createLantanaPatch, updateLantanaPatch, getLantanaPatches,
  getPaddocks, getUsers
} from '../../services/api'

const VEGETATION_TYPES = [
  { value: 'lantana', label: 'Lantana', color: 'text-red-700 bg-red-50' },
  { value: 'pasture', label: 'Pasture', color: 'text-green-700 bg-green-50' },
  { value: 'savannah_tree', label: 'Savannah Tree', color: 'text-amber-700 bg-amber-50' },
  { value: 'other', label: 'Other', color: 'text-gray-700 bg-gray-50' },
]

export default function LantanaPatchForm({ ranchId }) {
  const navigate = useNavigate()
  const { patchId } = useParams()
  const queryClient = useQueryClient()
  const isEditing = !!patchId

  const [formData, setFormData] = useState({
    paddock_id: '',
    vegetation_type: 'lantana',
    density: '',
    target_clear_date: '',
    estimated_duration_days: '',
    assigned_operator_id: '',
    location_description: '',
    // Edit-only fields
    actual_start_date: '',
    actual_end_date: '',
  })

  // Boundary coordinates (array of {lat, lng} points)
  const [coordinates, setCoordinates] = useState([
    { lat: '', lng: '' },
    { lat: '', lng: '' },
    { lat: '', lng: '' },
  ])

  const [error, setError] = useState('')
  const [calculatedArea, setCalculatedArea] = useState(null)

  // Fetch existing patch if editing
  const { data: existingPatch } = useQuery({
    queryKey: ['lantanaPatch', patchId],
    queryFn: async () => {
      const patches = await getLantanaPatches(ranchId)
      return patches.find(p => p.id === patchId)
    },
    enabled: isEditing,
  })

  // Fetch paddocks for dropdown
  const { data: paddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })

  // Fetch users for operator assignment
  const { data: users } = useQuery({
    queryKey: ['users', ranchId],
    queryFn: () => getUsers(ranchId),
  })

  // Filter for potential clearers. bush_clearer is the role that actually
  // performs lantana work; manager/owner included as fallbacks when a
  // bush_clearer isn't set up yet on a ranch.
  const operators = users?.filter(u =>
    ['bush_clearer', 'manager', 'owner'].includes(u.role_at_ranch) && u.is_active
  ) || []

  // Pre-fill form when editing
  useEffect(() => {
    if (existingPatch) {
      setFormData({
        paddock_id: existingPatch.paddock_id || '',
        vegetation_type: existingPatch.vegetation_type || 'lantana',
        density: existingPatch.density || '',
        target_clear_date: existingPatch.target_clear_date || existingPatch.scheduled_date || '',
        estimated_duration_days: existingPatch.estimated_duration_days || '',
        assigned_operator_id: existingPatch.assigned_operator_id || '',
        location_description: existingPatch.location_description || '',
        actual_start_date: existingPatch.actual_start_date || '',
        actual_end_date: existingPatch.actual_end_date || '',
      })
      // Load existing boundary coordinates if available
      if (existingPatch.boundary_coordinates && existingPatch.boundary_coordinates.length > 0) {
        setCoordinates(existingPatch.boundary_coordinates)
      }
    }
  }, [existingPatch])

  // Calculate area from coordinates using Shoelace formula
  useEffect(() => {
    const validCoords = coordinates.filter(c => c.lat && c.lng)
    if (validCoords.length >= 3) {
      const area = calculatePolygonArea(validCoords)
      setCalculatedArea(area)
    } else {
      setCalculatedArea(null)
    }
  }, [coordinates])

  // Shoelace formula for polygon area (in square meters, approximate)
  const calculatePolygonArea = (coords) => {
    const n = coords.length
    if (n < 3) return 0

    // Convert to numbers
    const points = coords.map(c => ({
      lat: parseFloat(c.lat),
      lng: parseFloat(c.lng)
    }))

    // Use Shoelace formula
    let area = 0
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      area += points[i].lng * points[j].lat
      area -= points[j].lng * points[i].lat
    }
    area = Math.abs(area) / 2

    // Convert from degrees² to approximate m² (rough conversion at equator)
    const metersPerDegree = 111320
    const areaM2 = area * metersPerDegree * metersPerDegree

    return areaM2
  }

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createLantanaPatch,
    onSuccess: () => {
      queryClient.invalidateQueries(['lantanaPatches'])
      navigate('/lantana')
    },
    onError: (err) => setError(err.response?.data?.detail || 'Failed to create patch'),
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateLantanaPatch(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['lantanaPatches'])
      navigate('/lantana')
    },
    onError: (err) => setError(err.response?.data?.detail || 'Failed to update patch'),
  })

  const addCoordinatePoint = () => {
    setCoordinates([...coordinates, { lat: '', lng: '' }])
  }

  const removeCoordinatePoint = (index) => {
    if (coordinates.length > 3) {
      setCoordinates(coordinates.filter((_, i) => i !== index))
    }
  }

  const updateCoordinate = (index, field, value) => {
    const newCoords = [...coordinates]
    newCoords[index][field] = value
    setCoordinates(newCoords)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    // Validation for new patches
    if (!isEditing) {
      if (!formData.paddock_id) {
        setError('Please select a paddock')
        return
      }

      if (!formData.density) {
        setError('Please select a density level')
        return
      }

      const validCoords = coordinates.filter(c => c.lat && c.lng)
      if (validCoords.length < 3) {
        setError('Please enter at least 3 coordinate points to define the patch boundary')
        return
      }
    }

    // Determine status based on whether operator and date are assigned
    let status = existingPatch?.status || 'identified'
    if (formData.assigned_operator_id && formData.target_clear_date) {
      status = 'scheduled'
    }

    // Generate patch code based on vegetation type (only for new patches)
    const prefixes = { lantana: 'LP', pasture: 'PP', savannah_tree: 'ST', other: 'VP' }
    const prefix = prefixes[formData.vegetation_type] || 'VP'
    const patchCode = `${prefix}-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`

    // Convert coordinates to numbers
    const validCoords = coordinates.filter(c => c.lat && c.lng)
    const numericCoords = validCoords.map(c => ({
      lat: parseFloat(c.lat),
      lng: parseFloat(c.lng)
    }))

    // Build payload - different fields for create vs update
    if (isEditing) {
      const payload = {
        assigned_operator_id: formData.assigned_operator_id || null,
        target_clear_date: formData.target_clear_date || null,
        scheduled_date: formData.target_clear_date || null,
        estimated_duration_days: formData.estimated_duration_days ? parseInt(formData.estimated_duration_days) : null,
        actual_start_date: formData.actual_start_date || null,
        actual_end_date: formData.actual_end_date || null,
        location_description: formData.location_description,
        status: status,
      }
      updateMutation.mutate({ id: patchId, data: payload })
    } else {
      const payload = {
        ranch_id: ranchId,
        paddock_id: formData.paddock_id,
        patch_code: patchCode,
        vegetation_type: formData.vegetation_type,
        density: formData.density,
        area_ha: calculatedArea ? (calculatedArea / 10000).toFixed(4) : null,
        estimated_area_m2: calculatedArea ? Math.round(calculatedArea) : null,
        identified_date: new Date().toISOString().split('T')[0],
        identification_source: 'manual',
        location_description: formData.location_description,
        assigned_operator_id: formData.assigned_operator_id || null,
        target_clear_date: formData.target_clear_date || null,
        scheduled_date: formData.target_clear_date || null,
        estimated_duration_days: formData.estimated_duration_days ? parseInt(formData.estimated_duration_days) : null,
        status: status,
        boundary_coordinates: numericCoords,
      }
      createMutation.mutate(payload)
    }
  }

  const selectedPaddock = paddocks?.find(p => p.id === formData.paddock_id)
  const selectedOperator = operators?.find(o => o.id === formData.assigned_operator_id)

  // Get existing coordinates for display when editing
  const existingCoords = existingPatch?.boundary_coordinates || []

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Schedule Patch Clearing' : 'Add Vegetation Patch'}
          </h1>
          <p className="text-gray-600">
            {isEditing ? 'Assign an operator and schedule clearing date' : 'Identify a new vegetation patch for clearing'}
          </p>
        </div>
      </div>

      {/* Workflow Info */}
      {!isEditing && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-green-900 mb-2">Adding a New Patch</h3>
          <ul className="text-sm text-green-800 space-y-1">
            <li>1. Select vegetation type and the paddock where it's located</li>
            <li>2. Enter boundary coordinates (minimum 3 points)</li>
            <li>3. Select density level (sparse/moderate/dense)</li>
            <li>4. Optionally assign an operator and target clearing date now, or do it later</li>
          </ul>
        </div>
      )}

      {/* Patch Info when editing */}
      {isEditing && existingPatch && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-blue-900 mb-2">Patch Details</h3>
          <div className="grid grid-cols-2 gap-4 text-sm text-blue-800">
            <div>
              <span className="text-blue-600">Code:</span> {existingPatch.patch_code}
            </div>
            <div>
              <span className="text-blue-600">Type:</span>{' '}
              <span className="capitalize">{existingPatch.vegetation_type || 'lantana'}</span>
            </div>
            <div>
              <span className="text-blue-600">Paddock:</span> {existingPatch.paddock_name || 'Unknown'}
            </div>
            <div>
              <span className="text-blue-600">Density:</span> {existingPatch.density}
            </div>
            <div>
              <span className="text-blue-600">Area:</span> {existingPatch.area_ha ? `${existingPatch.area_ha} ha` : 'Unknown'}
            </div>
            <div>
              <span className="text-blue-600">Status:</span> {existingPatch.status}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Vegetation Type + Paddock Selection - only for new patches */}
        {!isEditing && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Location & Type</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vegetation Type *
                </label>
                <select
                  required
                  value={formData.vegetation_type}
                  onChange={(e) => setFormData({ ...formData, vegetation_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  {VEGETATION_TYPES.map(vt => (
                    <option key={vt.value} value={vt.value}>{vt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paddock *
                </label>
                <select
                  required
                  value={formData.paddock_id}
                  onChange={(e) => setFormData({ ...formData, paddock_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select paddock</option>
                  {paddocks?.map(paddock => (
                    <option key={paddock.id} value={paddock.id}>
                      {paddock.code} - {paddock.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location Description
                </label>
                <input
                  type="text"
                  value={formData.location_description}
                  onChange={(e) => setFormData({ ...formData, location_description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Near the eastern fence line, by the water trough"
                />
              </div>
            </div>
          </div>
        )}

        {/* Boundary Coordinates - only for new patches */}
        {!isEditing && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Boundary Coordinates</h2>
              </div>
              <button
                type="button"
                onClick={addCoordinatePoint}
                className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                <Plus className="h-4 w-4" />
                Add Point
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Enter at least 3 GPS coordinate points to define the patch boundary.
              The area will be calculated automatically.
            </p>

            <div className="space-y-3">
              {coordinates.map((coord, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 w-8">#{index + 1}</span>
                  <input
                    type="number"
                    step="any"
                    placeholder="Latitude"
                    value={coord.lat}
                    onChange={(e) => updateCoordinate(index, 'lat', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  <input
                    type="number"
                    step="any"
                    placeholder="Longitude"
                    value={coord.lng}
                    onChange={(e) => updateCoordinate(index, 'lng', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  {coordinates.length > 3 && (
                    <button
                      type="button"
                      onClick={() => removeCoordinatePoint(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {calculatedArea && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-sm text-green-800">
                  <strong>Calculated Area:</strong> {(calculatedArea / 10000).toFixed(4)} hectares
                  ({Math.round(calculatedArea).toLocaleString()} m²)
                </div>
              </div>
            )}
          </div>
        )}

        {/* Show existing coordinates (read-only) when editing */}
        {isEditing && existingCoords.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Boundary Coordinates</h2>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                {existingCoords.length} coordinate points defined
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                {existingCoords.map((coord, index) => (
                  <div key={index} className="p-2 bg-white rounded border">
                    #{index + 1}: {parseFloat(coord.lat).toFixed(4)}, {parseFloat(coord.lng).toFixed(4)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Density - only for new patches */}
        {!isEditing && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <Leaf className="h-5 w-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Density</h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vegetation Density *
              </label>
              <select
                required
                value={formData.density}
                onChange={(e) => setFormData({ ...formData, density: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select density level</option>
                <option value="sparse">Sparse - Scattered plants, easy to clear</option>
                <option value="moderate">Moderate - Mixed coverage, medium effort</option>
                <option value="dense">Dense - Thick growth, significant effort required</option>
              </select>
            </div>
          </div>
        )}

        {/* Assignment & Scheduling */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Schedule Clearing' : 'Assignment (Optional)'}
            </h2>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            {isEditing
              ? 'Assign an operator and set a target clearing date for this patch.'
              : 'Assign an operator and set a target clearing date now, or leave blank to schedule later.'}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assign Operator {isEditing && '*'}
              </label>
              <select
                value={formData.assigned_operator_id}
                onChange={(e) => setFormData({ ...formData, assigned_operator_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                required={isEditing}
              >
                <option value="">Select operator{!isEditing && ' (optional)'}</option>
                {operators.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name} ({user.role_at_ranch})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Target Clearing Date {isEditing && '*'}
                </label>
                <input
                  type="date"
                  value={formData.target_clear_date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormData({ ...formData, target_clear_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  required={isEditing}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Estimated Duration (days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={formData.estimated_duration_days}
                  onChange={(e) => setFormData({ ...formData, estimated_duration_days: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., 3"
                />
              </div>
            </div>
          </div>

          {selectedOperator && formData.target_clear_date && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Status will be set to:</strong> Scheduled
              </p>
              <p className="text-sm text-blue-700 mt-1">
                {selectedOperator.full_name} will be notified to clear this patch by{' '}
                {new Date(formData.target_clear_date).toLocaleDateString()}
                {formData.estimated_duration_days && (
                  <> (estimated {formData.estimated_duration_days} day{formData.estimated_duration_days > 1 ? 's' : ''})</>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Actual Clearing Dates - only when editing */}
        {isEditing && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-amber-600" />
              <h2 className="text-lg font-semibold text-gray-900">Actual Clearing Dates</h2>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Record the actual start and end dates of clearing. Status updates automatically:
              setting a start date marks it as "clearing in progress", and setting an end date marks it as "cleared".
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Actual Start Date
                </label>
                <input
                  type="date"
                  value={formData.actual_start_date}
                  onChange={(e) => setFormData({ ...formData, actual_start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Actual End Date
                </label>
                <input
                  type="date"
                  value={formData.actual_end_date}
                  min={formData.actual_start_date || undefined}
                  onChange={(e) => setFormData({ ...formData, actual_end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>

            {formData.actual_start_date && !formData.actual_end_date && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Status will be updated to <strong>Clearing in Progress</strong>
              </div>
            )}
            {formData.actual_end_date && (
              <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                Status will be updated to <strong>Cleared</strong>
              </div>
            )}
          </div>
        )}

        {/* Location Description - when editing */}
        {isEditing && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Additional Notes</h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Description
              </label>
              <input
                type="text"
                value={formData.location_description}
                onChange={(e) => setFormData({ ...formData, location_description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="e.g., Near the eastern fence line, by the water trough"
              />
            </div>
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Save className="h-5 w-5" />
            {createMutation.isPending || updateMutation.isPending
              ? 'Saving...'
              : isEditing ? 'Save Changes' : 'Add Patch'}
          </button>
        </div>
      </form>
    </div>
  )
}
